
import React, { useState, useEffect, useRef } from 'react';

const DragonRaceTyping = () => {
  const [currentWord, setCurrentWord] = useState('DRAGON');
  const [userInput, setUserInput] = useState('');
  const [gameStats, setGameStats] = useState({
    wpm: 38,
    accuracy: 96,
    position: 2
  });
  const [playerProgress, setPlayerProgress] = useState(25);
  const [dragonProgress, setDragonProgress] = useState({
    fire: 35,
    ice: 15,
    storm: 45
  });
  const [isRacing, setIsRacing] = useState(true);
  
  const words = ['DRAGON', 'MAGIC', 'FIRE', 'STORM', 'WINGS', 'LIGHTNING', 'POWER', 'CRYSTAL', 'ENCHANT', 'MYSTIC'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setUserInput(value);
    
    if (value === currentWord) {
      const nextIndex = (currentWordIndex + 1) % words.length;
      setCurrentWordIndex(nextIndex);
      setCurrentWord(words[nextIndex]);
      setUserInput('');
      
      setPlayerProgress(prev => Math.min(prev + 5, 100));
      
      setGameStats(prev => ({
        ...prev,
        wpm: prev.wpm + Math.floor(Math.random() * 3),
        accuracy: Math.min(prev.accuracy + 1, 100)
      }));
    }
  };

  useEffect(() => {
    if (!isRacing) return;
    
    const interval = setInterval(() => {
      setDragonProgress(prev => ({
        fire: Math.min(prev.fire + Math.random() * 0.8, 100),
        ice: Math.min(prev.ice + Math.random() * 1.2, 100),
        storm: Math.min(prev.storm + Math.random() * 0.6, 100)
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [isRacing]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      height: '100vh',
      background: 'linear-gradient(180deg, #FF6B35 0%, #FF8E53 30%, #87CEEB 70%, #98FB98 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Game UI */}
      <div style={{
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          padding: '12px 20px',
          display: 'flex',
          gap: '20px',
          backdropFilter: 'blur(10px)',
          border: '2px solid #FF6B35',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#333' }}>{gameStats.wpm}</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>WPM</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#333' }}>{gameStats.accuracy}%</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Accuracy</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#333' }}>{gameStats.position}nd</div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Position</div>
          </div>
        </div>
        
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '16px',
          padding: '12px 20px',
          border: '2px solid #FF6B35'
        }}>
          <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#FF6B35' }}>Dragon Race</div>
        </div>
      </div>

      {/* Dragon Race Track */}
      <div style={{
        flex: 2,
        margin: '10px 20px 5px',
        position: 'relative',
        background: 'linear-gradient(180deg, #FFB347 0%, #87CEEB 40%, #32CD32 100%)',
        borderRadius: '20px',
        overflow: 'hidden',
        border: '4px solid #8B4513',
        boxShadow: '0 0 30px rgba(0,0,0,0.2)'
      }}>
        {/* Magical Sky Background */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '60%',
          background: 'linear-gradient(180deg, #FFB347 0%, #87CEEB 100%)',
          zIndex: 1
        }}>
          {/* Floating clouds */}
          <div style={{
            position: 'absolute',
            top: '10%',
            left: '20%',
            width: '60px',
            height: '20px',
            background: 'rgba(255,255,255,0.7)',
            borderRadius: '20px',
            boxShadow: '0 0 10px rgba(255,255,255,0.5)'
          }} />
          <div style={{
            position: 'absolute',
            top: '25%',
            right: '15%',
            width: '80px',
            height: '25px',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '25px',
            boxShadow: '0 0 15px rgba(255,255,255,0.4)'
          }} />
        </div>
        
        {/* Dragon Racing Lanes */}
        {[0, 1, 2, 3].map(laneIndex => (
          <div key={laneIndex} style={{
            position: 'absolute',
            left: '5%',
            right: '5%',
            height: '17.5%',
            bottom: `${laneIndex * 17.5}%`,
            borderBottom: laneIndex < 3 ? '3px dashed #FFD700' : 'none',
            display: 'flex',
            alignItems: 'center',
            padding: '0 40px',
            zIndex: 3
          }}>
            {laneIndex === 0 && (
              <div style={{
                position: 'relative',
                width: '100px',
                height: '60px',
                transform: `translateX(${Math.min(dragonProgress.storm * 4.5, 450)}px)`,
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))'
              }}>
                <div style={{
                  fontSize: '60px',
                  color: '#9B59B6',
                  textShadow: '0 0 20px #BB8FCE, 0 0 40px #D2B4DE'
                }}>🐉</div>
              </div>
            )}
            
            {laneIndex === 1 && (
              <div style={{
                position: 'relative',
                width: '100px',
                height: '60px',
                transform: `translateX(${Math.min(playerProgress * 4.5, 450)}px)`,
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))'
              }}>
                <div style={{
                  fontSize: '60px',
                  color: '#F1C40F',
                  textShadow: '0 0 20px #F7DC6F, 0 0 40px #FCF3CF'
                }}>🐲</div>
              </div>
            )}
            
            {laneIndex === 2 && (
              <div style={{
                position: 'relative',
                width: '100px',
                height: '60px',
                transform: `translateX(${Math.min(dragonProgress.fire * 4.5, 450)}px)`,
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))'
              }}>
                <div style={{
                  fontSize: '60px',
                  color: '#E74C3C',
                  textShadow: '0 0 20px #F1948A, 0 0 40px #FADBD8'
                }}>🔥🐲</div>
              </div>
            )}
            
            {laneIndex === 3 && (
              <div style={{
                position: 'relative',
                width: '100px',
                height: '60px',
                transform: `translateX(${Math.min(dragonProgress.ice * 4.5, 450)}px)`,
                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))'
              }}>
                <div style={{
                  fontSize: '60px',
                  color: '#3498DB',
                  textShadow: '0 0 20px #AED6F1, 0 0 40px #D6EAF8'
                }}>❄️🐲</div>
              </div>
            )}
          </div>
        ))}
        
        {/* Magical Finish Line */}
        <div style={{
          position: 'absolute',
          right: '50px',
          bottom: '0',
          width: '20px',
          height: '70%',
          background: 'linear-gradient(45deg, #FFD700 0%, #FFF 25%, #FFD700 50%, #FFF 75%, #FFD700 100%)',
          borderRadius: '10px',
          boxShadow: '0 0 30px #FFD700',
          border: '3px solid #FF6B35',
          zIndex: 4
        }} />
        
        {/* Dragon Race Leaderboard */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.9) 0%, rgba(160, 82, 45, 0.9) 100%)',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '15px',
          fontWeight: '700',
          border: '3px solid #FFD700',
          minWidth: '150px',
          zIndex: 5,
          boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#FFD700', fontSize: '1rem', textAlign: 'center' }}>
            🏆 DRAGON RACE
          </h4>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '0.9rem' }}>
            <span>1st</span>
            <span>🔥 Fire Dragon</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '5px', 
            fontSize: '0.9rem',
            color: '#FFD700',
            fontWeight: '800',
            background: 'rgba(255, 215, 0, 0.2)',
            padding: '2px 5px',
            borderRadius: '5px'
          }}>
            <span>2nd</span>
            <span>🐲 YOU</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '0.9rem' }}>
            <span>3rd</span>
            <span>⚡ Storm Dragon</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
            <span>4th</span>
            <span>❄️ Ice Dragon</span>
          </div>
        </div>
      </div>

      {/* Magical Typing Area */}
      <div style={{
        background: 'linear-gradient(135deg, #FFF8DC 0%, #F0E68C 100%)',
        borderRadius: '20px 20px 0 0',
        padding: '20px',
        textAlign: 'center',
        borderTop: '6px solid #FF6B35',
        boxShadow: '0 -15px 40px rgba(0,0,0,0.25)',
        minHeight: '160px'
      }}>
        <div style={{
          fontSize: '2.8rem',
          fontWeight: '900',
          color: '#FF6B35',
          marginBottom: '20px',
          textShadow: '0 4px 8px rgba(255, 107, 53, 0.3)',
          letterSpacing: '4px',
          fontFamily: 'Arial Black, sans-serif'
        }}>
          {currentWord}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={userInput}
          onChange={handleInputChange}
          placeholder="Type the magic word..."
          style={{
            width: '40%',
            maxWidth: '400px',
            padding: '18px 25px',
            fontSize: '1.8rem',
            border: '5px solid #FF6B35',
            borderRadius: '25px',
            outline: 'none',
            textAlign: 'center',
            fontWeight: '800',
            letterSpacing: '2px',
            background: 'white',
            fontFamily: 'Arial Black, sans-serif',
            boxShadow: '0 8px 20px rgba(255, 107, 53, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            e.target.style.borderColor = '#E55A2B';
            e.target.style.boxShadow = '0 0 30px rgba(255, 107, 53, 0.6)';
            e.target.style.transform = 'scale(1.04)';
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            e.target.style.borderColor = '#FF6B35';
            e.target.style.boxShadow = '0 8px 20px rgba(255, 107, 53, 0.2)';
            e.target.style.transform = 'scale(1)';
          }}
        />
      </div>
    </div>
  );
};

export default DragonRaceTyping;